import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import Form from 'antd/lib/form';
import { useForm } from 'antd/lib/form/Form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Typography from 'antd/lib/typography';
import { omit } from 'lodash';
import React, { useMemo, useState } from 'react';
import { DisplayedFilterFieldAndValue } from '../../../components/sidebar-filters/saved-filter/DisplayedFilterFieldAndValue';
import { ICreateSavedFilter, ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { ReadinessTrackingFilterLabels, TReadinessTrackingFilterKey } from './ReadinessTrackingSidebarFilters';
import { checkFilterInvalidValues, transformReadinessTrackingSavedFilterToUser } from './utils';

interface ISaveReadinessTrackingFiltersModalProps {
    isLoading: boolean;
    initialSavedFilter: ICreateSavedFilter;
    onCancel: () => void;
    onSave: (filter: ICreateSavedFilter) => Promise<void>;
}

interface ISaveReadinessTrackingFiltersFormValues {
    filter_name: string;
    description?: string;

    id: number | null;
    status: string;
    type: string;
    sub_type: string;
    display_in_dashboard?: boolean;
}

export const SaveReadinessTrackingFiltersModal: React.FC<ISaveReadinessTrackingFiltersModalProps> = ({
    initialSavedFilter,
    onSave,
    onCancel,
    isLoading,
}) => {
    const { notification } = App.useApp();
    const [form] = useForm<ISaveReadinessTrackingFiltersFormValues>();
    const context = useSidebarFilterOptionsContext();
    const [savedFilterValues, setSavedFilterValues] = useState<Record<string, any>>(initialSavedFilter?.filter_values || {});

    const handleRemoveFilterProperty = (filterKey: string) => {
        const validFilterValues = transformReadinessTrackingSavedFilterToUser(savedFilterValues, context);

        if (Object.keys(validFilterValues).length <= 1) {
            notification.warning({ key: 'unable-remove-filter-property', message: 'You must have at least one filter selected!' });
            return;
        }

        const updatedFilterValues = omit(savedFilterValues, filterKey);

        setSavedFilterValues(updatedFilterValues);
    };

    const handleFormSubmit = async (filterData: ISaveReadinessTrackingFiltersFormValues) => {
        await onSave({
            ...filterData,
            filter_values: savedFilterValues,
        });
    };

    const outdatedFields = useMemo(
        () => checkFilterInvalidValues(initialSavedFilter.filter_values, context),
        [context, initialSavedFilter.filter_values]
    );

    return (
        <Modal
            open
            destroyOnHidden
            title={initialSavedFilter.id ? 'Edit Filter Selections' : 'Save Filter Selections'}
            onCancel={() => onCancel()}
            footer={[
                <Button key="modal-cancel" onClick={() => onCancel()} loading={isLoading}>
                    Cancel
                </Button>,
                <Button type="primary" key="modal-save" onClick={() => form.submit()} loading={isLoading}>
                    Save
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" preserve={false} initialValues={initialSavedFilter || {}} onFinish={handleFormSubmit}>
                <Form.Item hidden name="id">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="status">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="type">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="sub_type">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="display_in_dashboard">
                    <Input />
                </Form.Item>

                <Form.Item
                    name="filter_name"
                    label={<Typography.Text strong>Name</Typography.Text>}
                    rules={[
                        { required: true, message: 'Please enter name!' },
                        { max: 100, message: 'Number of characters should be less than 100' },
                    ]}
                >
                    <Input placeholder="Filter Name" size="large" />
                </Form.Item>
                <Form.Item
                    name="description"
                    label={<Typography.Text strong>Description</Typography.Text>}
                    rules={[{ max: 255, message: 'Number of characters should be less than 255' }]}
                >
                    <Input.TextArea placeholder="Description" size="large" autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>

                <SelectedFilterValues
                    filterValues={savedFilterValues}
                    outdatedFields={outdatedFields}
                    onRemoveFilterProperty={handleRemoveFilterProperty}
                />
            </Form>
        </Modal>
    );
};

interface ISelectedFilterValuesProps {
    filterValues: ISavedFilter['filter_values'];
    outdatedFields: string[];
    onRemoveFilterProperty: (filterKey: string) => void;
}

const SelectedFilterValues: React.FC<ISelectedFilterValuesProps> = ({ filterValues, outdatedFields, onRemoveFilterProperty }) => {
    const context = useSidebarFilterOptionsContext();
    const displayedAllFilterValues = useMemo(() => transformReadinessTrackingSavedFilterToUser(filterValues, context), [filterValues, context]);
    const displayedFilterEntries = Object.entries(displayedAllFilterValues) as [TReadinessTrackingFilterKey, string][];

    return (
        <Flex vertical gap={12}>
            <Typography.Text strong>Selected Filters</Typography.Text>

            {displayedFilterEntries.length === 0 && <Typography.Text>-</Typography.Text>}

            <Flex wrap="wrap" gap={8}>
                {displayedFilterEntries.map(([key, value]) => (
                    <DisplayedFilterFieldAndValue
                        key={key}
                        fieldName={ReadinessTrackingFilterLabels[key]}
                        fieldValue={value}
                        isFieldOutdated={outdatedFields.includes(key)}
                        onClose={() => onRemoveFilterProperty(key)}
                    />
                ))}
            </Flex>
        </Flex>
    );
};
